import { useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { REMINDER_META, playChime } from '../utils/wellness';

// App-wide wellness reminder engine (mounted once in Layout). Purely client-side
// timing: ticks every 20s, fires dismissible toasts with optional soft chime.
// Per-user prefs come from /api/personal/wellness; last-fired/snooze state lives
// in localStorage keyed by user id so a page refresh doesn't restart the clocks.

const TICK_MS = 20000;
const SNOOZE_MS = 15 * 60 * 1000;
const PERIODIC = ['water', 'stretch', 'eyes'];
const ALL_TYPES = ['water', 'stretch', 'eyes', 'meals', 'nudge', 'hardstop'];

// LOCAL date — toISOString() is a day behind IST after 18:30
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function WellnessReminders() {
  const { user } = useAuth();
  const settingsRef = useRef(null);
  // My Day due-list cache — refreshed on date change and on pms:myday-changed
  const mydayRef = useRef({ date: null, due: [], loading: false });

  const key = useCallback(suffix => `wellness_${suffix}_${user?.id}`, [user?.id]);

  const loadMyday = useCallback(async () => {
    if (mydayRef.current.loading) return;
    mydayRef.current.loading = true;
    try {
      const today = todayStr();
      const r = await api.get(`/personal/routines/day?date=${today}`);
      mydayRef.current = { date: today, due: r.data.due || [], loading: false };
    } catch { mydayRef.current.loading = false; }
  }, []);

  const fire = useCallback(type => {
    const s = settingsRef.current;
    const meta = REMINDER_META[type];
    localStorage.setItem(key(`${type}_last`), String(Date.now()));
    if (s?.sound_enabled && meta.sound) playChime();
    toast.custom(t => (
      <div
        data-testid={`wellness-toast-${type}`}
        className="rounded-2xl shadow-lg px-4 py-3 flex items-start gap-3 max-w-sm"
        style={{ background: 'var(--card-bg, #fff)', border: '1px solid var(--card-border, #e5e7eb)' }}
      >
        <span className="text-xl flex-shrink-0">{meta.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary, #111)' }}>{meta.title}</div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary, #555)' }}>{meta.message}</div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="text-xs px-3 py-1 rounded-lg font-medium text-white"
              style={{ background: 'var(--btn-bg, #1e3a5f)' }}
            >
              Dismiss
            </button>
            <button
              onClick={() => {
                localStorage.setItem(key(`${type}_snooze`), String(Date.now() + SNOOZE_MS));
                toast.dismiss(t.id);
              }}
              className="text-xs px-3 py-1 rounded-lg font-medium border"
              style={{ color: 'var(--text-secondary, #555)', borderColor: 'var(--card-border, #e5e7eb)' }}
            >
              Snooze 15 min
            </button>
          </div>
        </div>
      </div>
    ), { id: `wellness-${type}`, duration: type === 'eyes' ? 12000 : 25000, position: 'bottom-right' });
  }, [key]);

  // My Day toasts share the wellness toast look; digest lists the day, item
  // toasts fire at each routine's set time. Same Dismiss + Snooze 15.
  const fireMyday = useCallback((kind, items) => {
    const s = settingsRef.current;
    if (s?.sound_enabled) playChime();
    const titles = items.map(i => i.title);
    const title = kind === 'digest' ? 'Today’s routines' : titles[0];
    const message = kind === 'digest'
      ? titles.slice(0, 4).join(' · ') + (titles.length > 4 ? ` +${titles.length - 4} more` : '')
      : `Scheduled for ${items[0].time_of_day}`;
    const snoozeKey = kind === 'digest' ? key('myday_morning_snooze') : key(`myday_item_${items[0].id}_snooze`);
    toast.custom(t => (
      <div
        data-testid={`myday-toast-${kind}`}
        className="rounded-2xl shadow-lg px-4 py-3 flex items-start gap-3 max-w-sm"
        style={{ background: 'var(--card-bg, #fff)', border: '1px solid var(--card-border, #e5e7eb)' }}
      >
        <span className="text-xl flex-shrink-0">☀️</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary, #111)' }}>{title}</div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary, #555)' }}>{message}</div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => toast.dismiss(t.id)} className="text-xs px-3 py-1 rounded-lg font-medium text-white" style={{ background: 'var(--btn-bg, #1e3a5f)' }}>Dismiss</button>
            <button onClick={() => { localStorage.setItem(snoozeKey, String(Date.now() + SNOOZE_MS)); toast.dismiss(t.id); }}
              className="text-xs px-3 py-1 rounded-lg font-medium border" style={{ color: 'var(--text-secondary, #555)', borderColor: 'var(--card-border, #e5e7eb)' }}>Snooze 15 min</button>
          </div>
        </div>
      </div>
    ), { id: kind === 'digest' ? 'myday-digest' : `myday-item-${items[0].id}`, duration: 25000, position: 'bottom-right' });
  }, [key]);

  const check = useCallback(() => {
    const s = settingsRef.current;
    if (!s || !user) return;
    const now = Date.now();
    const today = todayStr();

    // Day-start clock runs regardless of toggles, so enabling the hard-stop
    // mid-day still measures from when the app was first opened today
    let dayStart = null;
    try { dayStart = JSON.parse(localStorage.getItem(key('daystart'))); } catch {}
    if (!dayStart || dayStart.date !== today) {
      dayStart = { date: today, ts: now };
      localStorage.setItem(key('daystart'), JSON.stringify(dayStart));
    }

    if (s.paused) return;

    const enabled = type => !!s[type]?.enabled;

    // Snoozed reminders re-fire once their snooze expires
    for (const type of ALL_TYPES) {
      const snoozeAt = parseInt(localStorage.getItem(key(`${type}_snooze`)) || 0, 10);
      if (snoozeAt) {
        if (now >= snoozeAt && enabled(type)) {
          localStorage.removeItem(key(`${type}_snooze`));
          fire(type);
        }
        if (PERIODIC.includes(type)) continue; // wait out the snooze, skip regular check
      }
    }

    // Periodic: water / stretch / eyes
    for (const type of PERIODIC) {
      if (!enabled(type)) continue;
      if (localStorage.getItem(key(`${type}_snooze`))) continue;
      const last = parseInt(localStorage.getItem(key(`${type}_last`)) || 0, 10);
      if (!last) { localStorage.setItem(key(`${type}_last`), String(now)); continue; }
      if (now - last >= s[type].interval * 60000) fire(type);
    }

    // Meals: each slot fires once per day, only within an hour of its time
    if (enabled('meals')) {
      s.meals.times.forEach((tm, i) => {
        const slotKey = key(`meal${i}_date`);
        if (localStorage.getItem(slotKey) === today) return;
        const [h, m] = tm.split(':').map(Number);
        const target = new Date(); target.setHours(h, m, 0, 0);
        const diff = now - target.getTime();
        if (diff >= 0 && diff <= 60 * 60000) { localStorage.setItem(slotKey, today); fire('meals'); }
      });
    }

    // Daily nudge: once per day, within 3 hours of the set time
    if (enabled('nudge') && localStorage.getItem(key('nudge_date')) !== today) {
      const [h, m] = s.nudge.time.split(':').map(Number);
      const target = new Date(); target.setHours(h, m, 0, 0);
      const diff = now - target.getTime();
      if (diff >= 0 && diff <= 3 * 3600000) { localStorage.setItem(key('nudge_date'), today); fire('nudge'); }
    }

    // Hard-stop: once per day after N continuous hours since first open
    if (enabled('hardstop') && localStorage.getItem(key('hardstop_date')) !== today) {
      if (now - dayStart.ts >= s.hardstop.hours * 3600000) {
        localStorage.setItem(key('hardstop_date'), today);
        fire('hardstop');
      }
    }

    // ── My Day: morning digest + per-item time toasts ─────────────────────────
    const md = s.myday || {};
    if (md.morning_enabled || md.item_times_enabled) {
      if (mydayRef.current.date !== today) { loadMyday(); return; } // cache warms; next tick fires
      const due = mydayRef.current.due;

      if (md.morning_enabled) {
        const msn = parseInt(localStorage.getItem(key('myday_morning_snooze')) || 0, 10);
        if (msn) {
          if (now >= msn) {
            localStorage.removeItem(key('myday_morning_snooze'));
            const open = due.filter(i => i.tick_status !== 'done');
            if (open.length) fireMyday('digest', open);
          }
        } else if (localStorage.getItem(key('myday_morning_date')) !== today && due.length) {
          const [h, m] = (md.morning_time || '09:30').split(':').map(Number);
          const target = new Date(); target.setHours(h, m, 0, 0);
          const diff = now - target.getTime();
          if (diff >= 0 && diff <= 3 * 3600000) {
            localStorage.setItem(key('myday_morning_date'), today);
            fireMyday('digest', due);
          }
        }
      }

      if (md.item_times_enabled) {
        for (const item of due) {
          if (!item.time_of_day || item.tick_status === 'done') continue;
          const firedKey = key(`myday_item_${item.id}_date`);
          const snKey = key(`myday_item_${item.id}_snooze`);
          const sn = parseInt(localStorage.getItem(snKey) || 0, 10);
          if (sn) {
            if (now >= sn) { localStorage.removeItem(snKey); fireMyday('item', [item]); localStorage.setItem(firedKey, today); }
            continue;
          }
          if (localStorage.getItem(firedKey) === today) continue;
          const [h, m] = item.time_of_day.split(':').map(Number);
          const target = new Date(); target.setHours(h, m, 0, 0);
          if (now >= target.getTime()) { localStorage.setItem(firedKey, today); fireMyday('item', [item]); }
        }
      }
    }
  }, [user, key, fire, fireMyday, loadMyday]);

  // Load settings on login and whenever the Wellness tab saves changes
  useEffect(() => {
    if (!user) { settingsRef.current = null; return; }
    let alive = true;
    const load = () => api.get('/personal/wellness').then(r => { if (alive) settingsRef.current = r.data; }).catch(() => {});
    load();
    const onUpdate = () => load();
    const onMyday = () => loadMyday();
    window.addEventListener('pms:wellness-updated', onUpdate);
    window.addEventListener('pms:myday-changed', onMyday);
    return () => { alive = false; window.removeEventListener('pms:wellness-updated', onUpdate); window.removeEventListener('pms:myday-changed', onMyday); };
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const t = setInterval(check, TICK_MS);
    return () => clearInterval(t);
  }, [user?.id, check]);

  return null;
}
